import { ReactNode } from 'react';

import { Id, Maybe, Optional } from '@vimeo/core/types/generic';
import { Video } from '@vimeo/gctl-api/models/components/schemas/video/Video';

import { EPollState, IPoll } from './interaction_tools';
import { EAudioMixingMode } from './media';

/**
 * todo: Cleanup types/type naming to make difference between graphics items, descriptors and other things.
 */

export enum EGraphicsDescriptorType {
  LOWER_THIRD = 'lowerthird',
  BROADCASTER_LOWER_THIRD = 'broadcasterLowerthird',
  GUEST_LOWER_THIRD = 'guestLowerthird',
  POLL = 'poll',
  QNA = 'qna',
  LOGO = 'logo',
  IMAGE = 'fullscreen',
  AGORA_STREAM = 'agoraStream',
  AGORA_SCREEN = 'agoraScreen',
  AGORA_MEDIA = 'agoraMedia',
  SLIDES = 'slides',
}

export enum EGraphicsAnimation {
  FADE_IN = 'fade-in',
  LEFT_SLIDE = 'left-slide',
  RIGHT_SLIDE = 'right-slide',
  SELF_SLIDE = 'self-slide',
}

export enum EMediaStatus {
  PLAY = 'play',
  PAUSE = 'pause',
  RESTART = 'restart',
  STOP = 'stop',
}

export interface IBaseGraphicsPositionDescriptor {
  align: Optional<'topLeft' | 'bottomLeft'>;
  left: number;
  top: number;
  width: number;
  height: number;
  visible: boolean;
  index?: Maybe<number>;
  z: number;
}

export interface IAgoraGraphicsPositionDescriptor extends IBaseGraphicsPositionDescriptor {
  guestLabelScale: number;
}

export type IGraphicsPositionDescriptor =
  | IBaseGraphicsPositionDescriptor
  | IAgoraGraphicsPositionDescriptor;

export interface IBaseDescriptorData {
  sourceId: Id;
  visibleAt: number;
  createdAt: number;
}

/**
 * Slide graphic descriptor-representation related metadata.
 */
export interface ISlideDescriptorData extends IBaseDescriptorData {
  /**
   * Slide index for slides on a selected scene.
   */
  slideIndex?: number;
}

// todo: Add 'descriptor' in such typing since it is only scene representation of actual DB item.
export interface IGraphicsItem<D extends IBaseDescriptorData = IBaseDescriptorData> {
  type: EGraphicsDescriptorType;
  position: IGraphicsPositionDescriptor;
  data: D;
}

/**
 * Details containing information of current remote media stream from studio.
 */
export interface IMediaPlaybackInfo {
  timeRemaining: number;
  progressFraction: number;
  totalDuration: number;
  playingTime: number;
  seek: number;
}

/**
 * Meta description of current scene media.
 * Details about playback process in a selected scene.
 */
export interface IMetaMedia {
  /**
   * Status of media playback.
   */
  status: EMediaStatus;
  /**
   * Timestamp of video start in MS.
   * Used to indicate absolute timestamp when studio sets video as playing.
   */
  startedAt?: Maybe<number>;
  /**
   * Offset in MS related to start.
   * Used when video is started not from 00:00:00 or when studio receives input lag and we should correct time.
   * Can be negative or positive value.
   */
  offset?: Maybe<number>;

  /**
   * Used to indicate when media is repeating with endless replays.
   * 0/1/null/undefined/bool/invalid data -> no loop option.
   * -1 -> loop forever.
   * 2+ -> loop times count.
   */
  loop?: Maybe<number>;

  /**
   * Volume of the media
   * Min value is 0, max value is 1
   */
  volume?: Maybe<number>;

  /**
   * 0-1, value from which studio will start video.
   */
  seekOffset?: number;

  /**
   * Whether we should push next scene after video ending (next in scenes list).
   */
  nextOnEOF?: Maybe<boolean>;
}

/**
 * Metadata description of scene metadata and configuration.
 * Not related directly to scene content but configures the way it behaves.
 */
export interface IGraphicsSceneMetadata {
  /**
   * Layout screen sharing is active.
   * Ignored for auto-layout, for custom layout indicates whether PIP is enabled.
   */
  disableSpeakerPip?: Maybe<boolean>;
  /**
   * Position of speakers over screen sharing.
   */
  speakerPosition: IGraphicsPositionDescriptor;
  /**
   * Whether guest labels (lower thirds) are visible.
   */
  guestLabelsVisible: boolean;
  /**
   * Time when guests labels were triggered to show animation.
   */
  guestLabelsVisibleAt: number;
  /**
   * Media metadata.
   */
  media?: IMetaMedia;
  /**
   * Active layout type.
   */
  layoutType?: Maybe<string>;
  /**
   * Is scene marked for scheduled playback.
   */
  isScheduled?: Maybe<boolean>;
  /**
   * Is scene based on template.
   */
  isTemplateBased?: Maybe<boolean>;
  /**
   * Scene background color.
   */
  background: Maybe<string>;
}

export interface IGraphicsScene {
  /**
   * Scene id in standard firebase format.
   */
  id: string;
  /**
   * Scene name.
   * todo: Limitation and validation.
   */
  name: string;
  /**
   * Time of scene pushed to program.
   */
  pushedAt: number;
  /**
   * Graphics contained in a scene.
   * List of descriptors that describe graphics objects and link to database records containing actual info.
   */
  graphics: TSceneGraphicsList;
  /**
   *  Metadata for scene configuration and settings adjustments of whole object.
   */
  meta: Optional<IGraphicsSceneMetadata>;
}

export type TAnyGraphicsItem = IGraphicsItem;

export type TScenesList = Record<string, IGraphicsScene>;

export type TScenesOrder = Array<IGraphicsScene['id']>;

export type TSceneGraphicsList = Record<string, TAnyGraphicsItem>;

export type TSelectedVideoList = Record<string, Video>;

interface IBaseGraphicsItem {
  id: string;
  type: EGraphicsDescriptorType;
  createdAt: number;
  updatedAt?: number;
  pushedAt?: number;
  visibleAt?: number;
}

export enum ESlidesStatus {
  UPLOADED = 'UPLOADED',
  PROCESSING = 'PROCESSING',
  COMPLETE = 'COMPLETE',
  FAILED = 'FAILED',
}

export enum ESlidesType {
  PDF = '.pdf',
  PPT = '.ppt',
  PPTX = '.pptx',
}

export interface IGraphicsSlidesItem extends IBaseGraphicsItem {
  baseUrl: string;
  name: string;
  status: ESlidesStatus;
  fileUrl: string;
  slideIndex: number;
  thumbnails: Array<string>;
  slides: Array<string>;
  pagesCount?: number;
  pagesProcessed?: number;
}

export interface IGraphicsVideoItem extends IBaseGraphicsItem {
  type: EGraphicsDescriptorType.AGORA_MEDIA;
  error?: Maybe<string>;
  videoId: number;
  videoHash?: Maybe<string>;
  /**
   * Duration of video set in seconds.
   */
  videoDuration: number;
  videoThumb: string;
  videoThumbFullSize: string;
  hidden?: boolean;
}

export interface IGraphicsPollItem extends IPoll, IBaseGraphicsItem {
  type: EGraphicsDescriptorType.POLL;
  state: EPollState;
  options: Array<IGraphicsPollOption>;
  answer: string;
  roomId: Id;
}

export interface IGraphicsPollOption {
  id: string;
  text: string;
  correct: boolean;
  votesCount: number;
  isVotedByMe?: boolean;
}

export interface IGraphicsQuestionItem extends IBaseGraphicsItem {
  type: EGraphicsDescriptorType.QNA;
  id: string;
  line: string;
  imageSrc: string;
  subLine: string;
  votesCount: number;
  roomId: Id;
}

export interface IGraphicsLogoItem extends IBaseGraphicsItem {
  type: EGraphicsDescriptorType.LOGO;
  imageSrc: string;
}

export interface IGraphicsImageItem extends IBaseGraphicsItem {
  type: EGraphicsDescriptorType.IMAGE;
  imageSrc: string;
  line?: Maybe<string>;
  subLine?: Maybe<string>;
  hidden?: boolean;
}

export interface IGraphicsLowerThirdItem extends IBaseGraphicsItem {
  type: EGraphicsDescriptorType.LOWER_THIRD | EGraphicsDescriptorType.GUEST_LOWER_THIRD;
  id: string;
  line?: Maybe<string>;
  subLine?: Maybe<string>;
  imageSrc?: Maybe<string>;
}

export interface IBroadcasterLowerThirdItem extends IGraphicsLowerThirdItem {
  lastChangedBy: string;
}

// todo: Solve typing issue after complete implementation. Separate and correct objects everywhere.
export type IGraphic = {
  line?: Maybe<string>;
  subLine?: Maybe<string>;
  imageSrc?: Maybe<string>;
  fileUrl?: Maybe<string>;
  imageFileName?: Maybe<string>;
  // Video specific data
  videoId?: number;
  videoHash?: Maybe<string>;
  videoDuration?: number; // Video duration in seconds.
  videoThumb?: string;
  videoThumbFullSize?: string;
  // Qna specific data.
  votesCount?: string | number;
  // Object metadata.
  id: string;
  type: EGraphicsDescriptorType;
  createdAt: number;
  updatedAt?: number;
  disabled?: boolean;
  disabledAt?: number;
  roomId?: Id;
  // Slides specific data
  baseUrl?: string;
  name?: string;
  status?: ESlidesStatus;
  slideIndex?: number;
  thumbnails?: Array<string>;
  slides?: Array<string>;
  pagesCount?: number;
  pagesProcessed?: number;
  hidden?: boolean;
};

export type TGraphicBase<T = IGraphic> = Exclude<T, 'id' | 'createdAt'>;

export interface IPendingGraphic {
  name: Maybe<string>;
  id: string;
  type: EGraphicsDescriptorType;
  uploadingProgress: number;
}

export interface IGlobalLogo {
  id: string;
  visible?: boolean;
  visibleAt?: number;
  position: IBaseGraphicsPositionDescriptor;
}

export type TGraphicsList = Record<string, IGraphic>;

export type TPollGraphicsList = Record<string, IGraphicsPollItem>;

export type TSlidesGraphicsList = Record<string, IGraphicsSlidesItem>;

export interface IBroadcasterInfo {
  title: string;
  name: string;
}

/**
 * Theme:
 */
export interface IGraphicsTheme {
  font: EFontFamily;
  primaryColor: string;
  secondaryColor: string;
}

export enum EFontFamily {
  ROBOTO = 'Roboto',
}

export enum EAnimationState {
  INTRO = 'intro',
  OUTRO = 'outro',
  NONE = 'none',
}

export interface IGraphicsSnapshotRepresentation {
  default: string;
}

export interface IFileInfo {
  fileSrc: Optional<string | ArrayBuffer>;
  fileName: string;
}

export interface IGraphicsDimensions {
  x: number;
  y: number;
  w: number;
  h: number;
  e?: boolean;
}

/**
 * Snapshot describing DOM with JSON structure and absolute coordinates system.
 */
export interface IGraphicsTreeSnapshot extends IGraphicsDimensions {
  ovrfw?: string;
  // Background.
  'bg-c'?: string; // Background color.
  'bg-s'?: string; // Background size.
  'bg-p'?: string; // Background position.
  // Box shadow.
  bs?: string;
  // Animation.
  dl?: string;
  du?: string;
  // Text.
  cr?: string;
  ta?: string; // Text align.
  fw?: string; // Font weight.
  fs?: string; // Font size.
  lh?: string; // Line height.
  // Children.
  ch: Array<IGraphicsTreeSnapshot>;
  // Generic.
  [index: string]: string | boolean | number | Array<IGraphicsTreeSnapshot> | undefined | null;
}

export interface IDraggedGraphicData {
  id: string;
  graphicType: EGraphicsDescriptorType;
}

export interface IGraphicsCache {
  inProcess: number;
  totalLoads: number;
  timeSpent: number;
  queue: Set<string>;
  cacheLinks: Set<HTMLImageElement>;
  cacheUrls: Set<string>;
}

export enum ECachedSceneReason {
  NONE = 0,
  ACTIVE = 1,
  PREVIOUS,
  NEXT,
  PROGRAM,
  SECONDARY,
}

export interface ILayoutConfigItem {
  readonly id: string;
  readonly icon: ReactNode;
  readonly sources: Record<string, IGraphicsDimensions>;
}

export enum ENextScenePosition {
  FIRST = 0,
  PREVIOUS = 1,
  NEXT = 2,
  LAST = 3,
}

/**
 * Typing for firebase composer_settings.
 * Should be appropriately mapped before usage in managers.
 */
export interface IComposerSettings {
  logo: Optional<IGlobalLogo>;
  audio_mixing_mode: Optional<EAudioMixingMode>;
  is_recording_paused: Optional<boolean>;
  streaming_offset: Optional<number>;
  streaming_started_at: Optional<number>;
  is_local_mirrored: Optional<boolean>;
  scene_creation_increment: Optional<number>;
  is_studio_debug_enabled: Optional<boolean>;
}
